import _ from 'lodash'

export const useXhamster = () => {
	const {getFileNameFromUrl, extFetch} = useHelpers()

	/**
	 * Extract video URL from xhamster page
	 * Supports both direct video URLs and page URLs
	 */
	async function getVideoFromUrl(url) {
		try {
			// If it's already a direct video URL, return it
			if (url.match(/\.(mp4|webm|m3u8)(\?|$)/i)) {
				return {
					website: 'xhamster.com',
					type: 'video',
					url: url,
					fileName: getFileNameFromUrl(url) || 'video.mp4',
					previewUrl: url,
					thumbnail: null,
					canView: true,
					referer: url,
					headers: {
						Referer: url
					}
				}
			}

			// Otherwise, try to fetch the page and extract video URL
			try {
				let response = await extFetch(url, {
					method: 'GET',
					headers: {
						'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/143.0.0.0 Safari/537.36',
						'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8',
						'Accept-Language': 'en-US,en;q=0.9',
						'Connection': 'keep-alive',
					}
				})

			if (response && response.body) {
				let html = typeof response.body === 'string' ? response.body : (response.body.toString ? response.body.toString() : String(response.body))
				
				// Extract title for a meaningful file name
				let titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i)
				let videoTitle = titleMatch ? titleMatch[1].replace(/\s*[-|].*$/, '').trim() : ''
				if (!videoTitle) {
					try {
						let pathParts = new URL(url).pathname.split('/').filter(Boolean)
						videoTitle = pathParts[pathParts.length - 1]?.replace(/-/g, ' ') || ''
					} catch (e) {}
				}
				let fileName = videoTitle
					? videoTitle.replace(/[^\w\s.-]/g, '').replace(/\s+/g, '_').substring(0, 100) + '.mp4'
					: 'video.mp4'

				// Extract master m3u8 URL based on provided logic
				const masterUrlMatch = html.match(/https:[^"']+?_TPL_[^"']+?\.m3u8/) || html.match(/https:\\\/\\\/[^"']+?_TPL_[^"']+?\.m3u8/)
				
				if (masterUrlMatch) {
					let masterUrl = masterUrlMatch[0].replace(/\\/g, '')
					
					const multiMatch = masterUrl.match(/multi=([^/]+)/)
					let resolution = '720p'
					if (multiMatch) {
						const resolutions = multiMatch[1].split(',').map(r => r.split(':')[1]).filter(Boolean)
						const targetResolutions = ['1080p', '720p', '480p', '240p']
						for (const res of targetResolutions) {
							const found = resolutions.find(r => r.startsWith(res))
							if (found) {
								resolution = found
								break
							}
						}
					}

					const subPlaylistUrl = masterUrl.replace('_TPL_', resolution)
					
			return {
				website: 'xhamster.com',
				type: 'video',
				url: subPlaylistUrl,
				fileName,
				previewUrl: subPlaylistUrl,
				thumbnail: null,
				canView: true,
				quality: resolution,
				hls: true,
				referer: url,
				headers: {
					Referer: url
				}
			}
					}

					// Fallback extraction
					let videoMatch = html.match(/<video[^>]*src=["']([^"']+)["']/i) || 
									  html.match(/<source[^>]*src=["']([^"']+)["']/i) ||
									  html.match(/videoUrl["']?\s*[:=]\s*["']([^"']+)["']/i) ||
									  html.match(/video_url["']?\s*[:=]\s*["']([^"']+)["']/i) ||
									  html.match(/["'](https?:\/\/[^"']+\.(mp4|webm|m3u8)[^"']*)["']/i)
					
					if (videoMatch && videoMatch[1]) {
						let videoUrl = videoMatch[1].replace(/\\/g, '')
						if (videoUrl.startsWith('//')) videoUrl = 'https:' + videoUrl
						
				return {
					website: 'xhamster.com',
					type: 'video',
					url: videoUrl,
					fileName: getFileNameFromUrl(videoUrl) || 'video.mp4',
					previewUrl: videoUrl,
					thumbnail: null,
					canView: true,
					hls: videoUrl.includes('.m3u8'),
					referer: url,
					headers: {
						Referer: url
					}
				}
					}
				}
			} catch (fetchError) {
				console.warn('Could not fetch xhamster page to extract video URL:', fetchError)
			}

			// Fallback: return the original URL as-is
			return {
				website: 'xhamster.com',
				type: 'video',
				url: url,
				fileName: getFileNameFromUrl(url) || 'video.mp4',
				previewUrl: url,
				thumbnail: null,
				canView: true,
				referer: url,
				headers: {
					Referer: url
				}
			}
		} catch (error) {
			console.error('Error fetching xhamster video from URL:', error)
			return {
				website: 'xhamster.com',
				type: 'video',
				url: url,
				fileName: getFileNameFromUrl(url) || 'video.mp4',
				previewUrl: url,
				thumbnail: null,
				canView: true
			}
		}
	}

	async function getVideosFromUrls(urls) {
		let validUrls = urls
			.split('\n')
			.map(url => url.trim())
			.filter(url => url && url.length > 0)

		const CONCURRENCY = 5
		const results = new Array(validUrls.length)
		let nextIndex = 0

		const worker = async () => {
			while (nextIndex < validUrls.length) {
				const idx = nextIndex++
				results[idx] = await getVideoFromUrl(validUrls[idx])
			}
		}

		await Promise.all(
			Array.from({length: Math.min(CONCURRENCY, validUrls.length)}, () => worker())
		)

		return results.map((media, index) => ({
			...media,
			id: `xhamster-${Date.now()}-${index}`,
			source: 'xhamster'
		}))
	}

	return {
		getMedia: async (form) => {
			if (form.urls) return getVideosFromUrls(form.urls)
			if (form.url) {
				let media = await getVideoFromUrl(form.url)
				media.id = `xhamster-${Date.now()}`
				media.source = 'xhamster'
				return [media]
			}
			return []
		},
		getVideoFromUrl,
		getVideosFromUrls
	}
}
